/**
 * Step 3: Targeting
 * 
 * Define where and what this promo applies to
 * - Promo applies to: All, Specific, or Exclude
 * - Entity Type: Based on domain from Step 1 (read-only)
 * - Select Entity: sellers (Shop) | academies (Academy) | venues (Booking)
 * - Select Sub-entity: locations (Shop) | coaching centers (Academy) | N/A (Booking)
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import { PromoFormData, PromoValidationErrors, EntityTargetingMode, PromoDomain, ScopeFilter } from '../../../../../api/promos/types';
import { getSellers, getSellerLocations } from '../../../../../api/shop';
import { Seller, SellerLocation } from '../../../../../api/shop/types/sellerTypes';
import { CatalogueConfig } from '../../../../../api/shop/types/productTypes';
import { getCatalogueConfig } from '../../../../../api/shop/products';
import { listAcademiesForScope, listCenterNamesForAcademy, AcademyListItem, CenterNameItem } from '../../../../../api/academy/academyService';
import { listVenuesForScope, VenueListItem } from '../../../../../api/venue/venueService';
import { getImageUrl } from '../../../../../utils/imageUrl';
import '../../../Campaigns/CreateCampaign/components/StepperButtons.css';
import '../../../Campaigns/CreateCampaign/components/StepperFormStyles.css';
import './StepTargeting.css';

interface StepTargetingProps {
  formData: PromoFormData;
  validationErrors: PromoValidationErrors;
  onChange: (updates: Partial<PromoFormData>) => void;
  onNext: () => void;
  onPrevious: () => void;
}

// Targeting mode enum for UI
enum TargetingModeUI {
  ALL = 'ALL',
  SPECIFIC = 'SPECIFIC',
  EXCLUDE = 'EXCLUDE',
}

// Sub-entity option shape with parentId for pruning
interface SubEntityOption {
  value: string;
  label: string;
  parentId: string;
}

const StepTargeting: React.FC<StepTargetingProps> = ({
  formData,
  validationErrors,
  onChange,
  onNext,
  onPrevious,
}) => {
  const isAcademyDomain = formData.domain === PromoDomain.ACADEMY;
  const isBookingDomain = formData.domain === PromoDomain.BOOKING;

  // ---- Shop / generic state ----
  const [availableSellers, setAvailableSellers] = useState<Seller[]>([]);
  const [availableLocations, setAvailableLocations] = useState<SellerLocation[]>([]);

  // ---- Academy state ----
  const [availableAcademies, setAvailableAcademies] = useState<AcademyListItem[]>([]);
  // Centers store the API item plus the academyId used when fetching, so we know the parent
  const [availableCenters, setAvailableCenters] = useState<(CenterNameItem & { academyId: string })[]>([]);

  // ---- Booking / venue state ----
  const [availableVenues, setAvailableVenues] = useState<VenueListItem[]>([]);

  // Shared loading flags (reused across domains)
  const [sellersLoading, setSellersLoading] = useState(false);
  const [locationsLoading, setLocationsLoading] = useState(false);

  const [showFilterValidation, setShowFilterValidation] = useState(false);

  // Catalogue config for sports & categories (for refine filters)
  const [catalogueConfig, setCatalogueConfig] = useState<CatalogueConfig | null>(null);
  const [catalogueLoading, setCatalogueLoading] = useState(false);

  // Determine current targeting mode from formData
  const getCurrentTargetingMode = (): TargetingModeUI => {
    if (!formData.scope?.entityTargeting) {
      return TargetingModeUI.ALL;
    }
    if (formData.scope.entityTargeting.mode === EntityTargetingMode.INCLUDE) {
      return TargetingModeUI.SPECIFIC;
    }
    if (formData.scope.entityTargeting.mode === EntityTargetingMode.EXCLUDE) {
      return TargetingModeUI.EXCLUDE;
    }
    return TargetingModeUI.ALL;
  };

  const currentMode = getCurrentTargetingMode();

  // Get entity type label based on domain
  const getEntityTypeLabel = (): string => {
    switch (formData.domain) {
      case PromoDomain.SHOP:
        return 'Shop';
      case PromoDomain.ACADEMY:
        return 'Academy';
      case PromoDomain.BOOKING:
        return 'Booking';
      case PromoDomain.COACHING:
        return 'Coaching';
      default:
        return 'Entity';
    }
  };

  // Fetch entities (sellers or academies) when mode or domain changes
  useEffect(() => {
    if (currentMode === TargetingModeUI.ALL) return;

    const fetchEntities = async () => {
      try {
        setSellersLoading(true);

        if (isAcademyDomain) {
          setAvailableSellers([]);
          setAvailableVenues([]);
          const academies = await listAcademiesForScope();
          setAvailableAcademies(academies);
        } else if (isBookingDomain) {
          setAvailableSellers([]);
          setAvailableAcademies([]);
          const venues = await listVenuesForScope();
          setAvailableVenues(venues);
        } else {
          setAvailableAcademies([]);
          setAvailableVenues([]);
          const response = await getSellers({
            pageNum: 1,
            status: 'ACTIVE',
          });
          setAvailableSellers(response.paginatedResults || []);
        }
      } catch (error) {
        if (isAcademyDomain) {
          setAvailableAcademies([]);
        } else if (isBookingDomain) {
          setAvailableVenues([]);
        } else {
          setAvailableSellers([]);
        }
      } finally {
        setSellersLoading(false);
      }
    };

    fetchEntities();
  }, [currentMode, formData.domain]);

  // Fetch catalogue config once (for sports & categories in refine filters)
  useEffect(() => {
    const fetchCatalogue = async () => {
      try {
        setCatalogueLoading(true);
        const cfg = await getCatalogueConfig();
        setCatalogueConfig(cfg);
      } catch (error) {
        setCatalogueConfig(null);
      } finally {
        setCatalogueLoading(false);
      }
    };

    fetchCatalogue();
  }, []);

  // Fetch sub-entities (locations or coaching centers) for selected entities
  useEffect(() => {
    const selectedEntityIds = formData.scope?.entityTargeting?.entityIds || [];

    if (currentMode === TargetingModeUI.ALL || selectedEntityIds.length === 0 || isBookingDomain) {
      setAvailableLocations([]);
      setAvailableCenters([]);
      return;
    }

    const fetchSubEntities = async () => {
      try {
        setLocationsLoading(true);

        if (isAcademyDomain) {
          // Reset shop state
          setAvailableLocations([]);
          const allCenters: (CenterNameItem & { academyId: string })[] = [];
          for (const academyId of selectedEntityIds) {
            try {
              const centers = await listCenterNamesForAcademy(academyId);
              allCenters.push(...centers.map(c => ({ ...c, academyId })));
            } catch {
              // continue on individual failure
            }
          }
          setAvailableCenters(allCenters);
        } else {
          // Reset academy state
          setAvailableCenters([]);
          const allLocations: SellerLocation[] = [];
          for (const sellerId of selectedEntityIds) {
            try {
              const response = await getSellerLocations({
                seller: sellerId,
                status: 'ACTIVE',
                pageNum: 1,
              });
              if (response.paginatedResults) {
                allLocations.push(...response.paginatedResults);
              }
            } catch {
              // continue on individual failure
            }
          }
          setAvailableLocations(allLocations);
        }
      } catch (error) {
        setAvailableLocations([]);
        setAvailableCenters([]);
      } finally {
        setLocationsLoading(false);
      }
    };

    fetchSubEntities();
  }, [formData.scope?.entityTargeting?.entityIds, currentMode, formData.domain, isBookingDomain]);

  // Handle targeting mode change
  const handleModeChange = (mode: TargetingModeUI) => {
    if (mode === TargetingModeUI.ALL) {
      onChange({
        scope: {
          ...formData.scope,
          entityTargeting: undefined,
        }
      });
    } else {
      const entityTargetingMode = mode === TargetingModeUI.SPECIFIC
        ? EntityTargetingMode.INCLUDE
        : EntityTargetingMode.EXCLUDE;

      onChange({
        scope: {
          ...formData.scope,
          entityTargeting: {
            mode: entityTargetingMode,
            entityIds: formData.scope?.entityTargeting?.entityIds || [],
            subEntityIds: formData.scope?.entityTargeting?.subEntityIds || [],
          }
        }
      });
    }
  };

  // ---- Domain-aware options ----

  const entityOptions = useMemo(() => {
    if (isAcademyDomain) {
      return availableAcademies.map(a => ({
        value: a._id,
        label: a.basicDetails.academyName,
        parentId: '',
      }));
    }
    if (isBookingDomain) {
      return availableVenues.map(v => ({
        value: v._id,
        label: v.basicDetails.name,
        parentId: '',
      }));
    }
    return availableSellers.map(seller => ({
      value: seller._id || seller.id,
      label: seller.name,
      parentId: '',
    }));
  }, [isAcademyDomain, isBookingDomain, availableAcademies, availableVenues, availableSellers]);

  const subEntityOptions = useMemo((): SubEntityOption[] => {
    if (isAcademyDomain) {
      return availableCenters.map(c => {
        const academyName = availableAcademies.find(a => a._id === c.academyId)?.basicDetails.academyName;
        return {
          value: c._id,
          label: `${c.basicDetails.location} (${academyName || 'Unknown'})`,
          parentId: c.academyId,
        };
      });
    }
    return availableLocations.map(location => {
      const sellerRef = location.seller as any;
      const sellerId = typeof sellerRef === 'string' ? sellerRef : sellerRef?.id || sellerRef?._id;
      const seller = availableSellers.find(s => (s._id || s.id) === sellerId);
      const sellerName = seller?.name || 'Unknown';
      const locationLabel = location.name || `${location.locationCode} - ${location.address?.city || ''}`;
      return {
        value: location._id,
        label: `${locationLabel} (${sellerName})`,
        parentId: sellerId,
      };
    });
  }, [isAcademyDomain, availableCenters, availableAcademies, availableLocations, availableSellers]);

  const selectedEntities = useMemo(() => {
    const selectedIds = formData.scope?.entityTargeting?.entityIds || [];
    return entityOptions.filter(opt => selectedIds.includes(opt.value));
  }, [entityOptions, formData.scope?.entityTargeting?.entityIds]);

  const selectedSubEntities = useMemo(() => {
    const selectedIds = formData.scope?.entityTargeting?.subEntityIds || [];
    return subEntityOptions.filter(opt => selectedIds.includes(opt.value));
  }, [subEntityOptions, formData.scope?.entityTargeting?.subEntityIds]);

  // ---- Handlers ----

  const handleEntityChange = (selectedOptions: any) => {
    const selectedIds: string[] = selectedOptions ? selectedOptions.map((opt: any) => opt.value) : [];
    const selectionSet = new Set(selectedIds);

    // Prune sub-entities whose parent is no longer selected
    const currentSubIds = formData.scope?.entityTargeting?.subEntityIds || [];
    const filteredSubIds = currentSubIds.filter(subId => {
      const subOpt = subEntityOptions.find(o => o.value === subId);
      return subOpt ? selectionSet.has(subOpt.parentId) : false;
    });

    onChange({
      scope: {
        ...formData.scope,
        entityTargeting: {
          ...formData.scope?.entityTargeting,
          mode: currentMode === TargetingModeUI.SPECIFIC
            ? EntityTargetingMode.INCLUDE
            : EntityTargetingMode.EXCLUDE,
          entityIds: selectedIds,
          subEntityIds: filteredSubIds,
        },
      },
    });
  };

  const handleRemoveEntity = (entityId: string) => {
    const updatedEntities = selectedEntities.filter(o => o.value !== entityId);
    handleEntityChange(updatedEntities);
  };

  const handleSubEntityChange = (selectedOptions: any) => {
    const selectedIds = selectedOptions ? selectedOptions.map((opt: any) => opt.value) : [];

    onChange({
      scope: {
        ...formData.scope,
        entityTargeting: {
          ...formData.scope?.entityTargeting,
          mode: currentMode === TargetingModeUI.SPECIFIC
            ? EntityTargetingMode.INCLUDE
            : EntityTargetingMode.EXCLUDE,
          entityIds: formData.scope?.entityTargeting?.entityIds || [],
          subEntityIds: selectedIds,
        }
      }
    });
  };

  const handleRemoveSubEntity = (subEntityId: string) => {
    const updatedSubEntities = selectedSubEntities.filter(o => o.value !== subEntityId);
    handleSubEntityChange(updatedSubEntities);
  };

  // React-select styles
  const getSelectStyles = () => ({
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    control: (base: any) => ({
      ...base,
      height: '38px',
      minHeight: '38px',
      border: '1px solid #e3e6f0',
      borderRadius: '6px',
      fontSize: '14px',
      boxShadow: 'none',
      width: '100%',
      borderColor: '#e3e6f0',
      '&:hover': {
        borderColor: '#e3e6f0'
      }
    }),
    valueContainer: (base: any) => ({
      ...base,
      height: '36px',
      padding: '0 8px',
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      height: '36px',
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      padding: '0 8px',
    }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: '#F5FAFF',
      border: '1px solid #B0D5FF',
      borderRadius: '4px',
    }),
    multiValueLabel: (base: any) => ({
      ...base,
      color: '#212529',
      fontSize: '12px',
      padding: '4px 8px',
    }),
    multiValueRemove: (base: any) => ({
      ...base,
      color: '#6c757d',
      '&:hover': {
        backgroundColor: '#B0D5FF',
        color: '#212529',
      }
    }),
  });

  // ------- Refine Filters (Step: "Refine what this promo applies to") -------
  const filters = formData.scope?.filters || [];
  const maxFilters = 2;
  const canAddFilter = filters.length < maxFilters;

  // Field options for filters (only Sport and Catalogue Category)
  const fieldOptions = [
    { value: 'SPORT', label: 'Sport' },
    { value: 'CATALOG_CATEGORY', label: 'Catalogue Category' },
  ];

  // Map UI field values to API field names
  const mapFieldToApi = (field: string): string => {
    if (field === 'SPORT') return 'sport';
    if (field === 'CATALOG_CATEGORY') return 'catalogueCategory';
    return field;
  };

  // Map API field names to UI field values
  const mapApiToField = (field: string): string => {
    if (field === 'sport') return 'SPORT';
    if (field === 'catalogueCategory') return 'CATALOG_CATEGORY';
    return field;
  };

  const sportFilterOptions = useMemo(
    () =>
      (catalogueConfig?.sports || []).map((sport) => ({
        value: sport.value,
        label: sport.label,
      })),
    [catalogueConfig]
  );

  const categoryFilterOptions = useMemo(() => {
    if (!catalogueConfig) return [];
    const { categories } = catalogueConfig;
    const app = categories?.apparels || [];
    const acc = categories?.accessories || [];
    return [...app, ...acc].map((cat) => ({
      value: cat.value,
      label: cat.label,
    }));
  }, [catalogueConfig]);

  // Get available field for second filter (opposite of first filter)
  const getAvailableFieldForSecondFilter = (): string | null => {
    if (filters.length === 0) {
      return 'SPORT';
    }
    const firstFilterField = mapApiToField(filters[0]?.field || '');
    if (firstFilterField === 'SPORT') return 'CATALOG_CATEGORY';
    if (firstFilterField === 'CATALOG_CATEGORY') return 'SPORT';
    return null;
  };

  const handleAddFilter = () => {
    if (!canAddFilter) return;

    if (filters.length === 0) {
      const newFilter: ScopeFilter = {
        field: mapFieldToApi('SPORT'),
        operator: 'IN',
        value: [],
      };
      onChange({
        scope: {
          ...formData.scope,
          filters: [newFilter],
        },
      });
      return;
    }

    const availableField = getAvailableFieldForSecondFilter();
    if (!availableField) return;

    const newFilter: ScopeFilter = {
      field: mapFieldToApi(availableField),
      operator: 'IN',
      value: [],
    };

    onChange({
      scope: {
        ...formData.scope,
        filters: [...filters, newFilter],
      },
    });
  };

  const handleRemoveFilter = (index: number) => {
    const updatedFilters = filters.filter((_, i) => i !== index);
    onChange({
      scope: {
        ...formData.scope,
        filters: updatedFilters,
      },
    });
  };

  const handleFilterFieldChange = (index: number, selectedOption: any) => {
    const field = selectedOption?.value;
    const updatedFilters = [...filters];

    if (!field) {
      updatedFilters[index] = {
        field: '',
        operator: 'IN',
        value: [],
      };
    } else {
      updatedFilters[index] = {
        field: mapFieldToApi(field),
        operator: 'IN',
        value: [],
      };
    }

    onChange({
      scope: {
        ...formData.scope,
        filters: updatedFilters,
      },
    });
  };

  const handleFilterValueChange = (index: number, options: any) => {
    const values = (options || []).map((opt: any) => opt.value);
    const currentFilter = filters[index];
    if (!currentFilter) return;

    const updatedFilters = [...filters];
    updatedFilters[index] = {
      ...currentFilter,
      value: values,
    };

    onChange({
      scope: {
        ...formData.scope,
        filters: updatedFilters,
      },
    });
  };

  // Get selected options for a filter
  const getSelectedFilterOptions = (filter: ScopeFilter) => {
    if (!Array.isArray(filter.value)) return [];
    const field = mapApiToField(filter.field);
    const source =
      field === 'SPORT'
        ? sportFilterOptions
        : field === 'CATALOG_CATEGORY'
        ? categoryFilterOptions
        : [];
    return source.filter((opt) => (filter.value as (string | number)[]).includes(opt.value));
  };

  // Validate filters and entity selection
  const isFiltersValid = useMemo(() => {
    if (currentMode === TargetingModeUI.SPECIFIC || currentMode === TargetingModeUI.EXCLUDE) {
      const entityIds = formData.scope?.entityTargeting?.entityIds || [];
      if (entityIds.length === 0) {
        return false;
      }
    }

    if (!filters || filters.length === 0) return true;

    return filters.every(filter => {
      if (filter.field && filter.field.trim() !== '') {
        const values = Array.isArray(filter.value) ? filter.value : [];
        return values.length > 0;
      }
      return true;
    });
  }, [filters, currentMode, formData.scope?.entityTargeting?.entityIds]);

  // Derive placeholder text based on domain
  const entityPlaceholder = sellersLoading
    ? (isAcademyDomain ? 'Loading academies...' : isBookingDomain ? 'Loading venues...' : 'Loading sellers...')
    : (isAcademyDomain ? 'Select Academy' : isBookingDomain ? 'Select Venue' : 'Select Entity');

  const subEntityPlaceholder = isBookingDomain
    ? 'Not applicable for Booking'
    : !formData.scope?.entityTargeting?.entityIds?.length
      ? 'Select an entity first'
      : locationsLoading
        ? (isAcademyDomain ? 'Loading centers...' : 'Loading locations...')
        : (isAcademyDomain
            ? subEntityOptions.length === 0 ? 'No centers available' : 'Select Coaching Center'
            : subEntityOptions.length === 0 ? 'No locations available' : 'Select Sub-entity');

  return (
    <div>
      <div className="mb-4">
        <h5 className="mb-2">Targeting</h5>
        <p className="text-muted mb-0">Define where and what this promo applies to</p>
      </div>

      {/* Promo applies to section */}
      <div className={`targeting-section-card ${currentMode === TargetingModeUI.ALL ? 'targeting-section-card-compact' : ''}`}>
        <Form.Group className="stepper-form-group">
          <Form.Label className="stepper-form-label">Promo applies to</Form.Label>
          <div className="d-flex gap-4 mt-2">
            <Form.Check
              type="radio"
              id="targeting-all"
              label="All"
              checked={currentMode === TargetingModeUI.ALL}
              onChange={() => handleModeChange(TargetingModeUI.ALL)}
              className="stepper-radio"
            />
            <Form.Check
              type="radio"
              id="targeting-specific"
              label="Specific"
              checked={currentMode === TargetingModeUI.SPECIFIC}
              onChange={() => handleModeChange(TargetingModeUI.SPECIFIC)}
              className="stepper-radio"
            />
            <Form.Check
              type="radio"
              id="targeting-exclude"
              label="Exclude"
              checked={currentMode === TargetingModeUI.EXCLUDE}
              onChange={() => handleModeChange(TargetingModeUI.EXCLUDE)}
              className="stepper-radio"
            />
          </div>
        </Form.Group>

        {/* Entity Type (read-only, based on domain) */}
        <Row className={currentMode === TargetingModeUI.ALL ? "mt-2" : "mt-3"}>
          <Col lg={12} className={currentMode === TargetingModeUI.ALL ? "mb-0" : "mb-3"}>
            <Form.Group className="stepper-form-group">
              <Form.Label className="stepper-form-label">
                Entity Type (Based on domain)
              </Form.Label>
              <Form.Control
                type="text"
                className="stepper-form-control stepper-form-control-disabled"
                value={getEntityTypeLabel()}
                disabled
                readOnly
              />
            </Form.Group>
          </Col>
        </Row>

        {/* Select Entity + Select Sub-entity — shown for Specific/Exclude */}
        {currentMode !== TargetingModeUI.ALL && (
          <Row className="mt-0">
            <Col lg={6} className="mb-3">
              <Form.Group className="stepper-form-group">
                <Form.Label className="stepper-form-label">
                  Select Entity <span className="text-danger">*</span>
                </Form.Label>
                <Select
                  styles={getSelectStyles()}
                  options={entityOptions.filter(opt => !selectedEntities.some(sel => sel.value === opt.value))}
                  value={null}
                  onChange={(selectedOption: any) => {
                    if (selectedOption) {
                      const currentIds = formData.scope?.entityTargeting?.entityIds || [];
                      if (!currentIds.includes(selectedOption.value)) {
                        handleEntityChange([...selectedEntities, selectedOption]);
                      }
                    }
                  }}
                  placeholder={entityPlaceholder}
                  isMulti={false}
                  isSearchable
                  menuPortalTarget={document.body}
                  isLoading={sellersLoading}
                />
                {validationErrors.scope?.entityTargeting && (
                  <div className="stepper-invalid-feedback">
                    {validationErrors.scope.entityTargeting}
                  </div>
                )}
                {selectedEntities.length > 0 && (
                  <div className="targeting-selected-chips mt-2">
                    {selectedEntities.map((opt) => (
                      <span key={opt.value} className="targeting-chip">
                        {opt.label}
                        <button
                          type="button"
                          className="targeting-chip-remove"
                          onClick={() => handleRemoveEntity(opt.value)}
                          aria-label={`Remove ${opt.label}`}
                        >
                          <i className="ph-x"></i>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </Form.Group>
            </Col>

            {/* Select Sub-entity */}
            <Col lg={6} className="mb-3">
              <Form.Group className="stepper-form-group">
                <Form.Label className="stepper-form-label">
                  Select Sub-entity <span className="text-muted">(Optional)</span>
                </Form.Label>
                <Select
                  styles={getSelectStyles()}
                  options={subEntityOptions.filter(opt => !selectedSubEntities.some(loc => loc.value === opt.value))}
                  value={null}
                  onChange={(selectedOption: any) => {
                    if (selectedOption) {
                      const currentIds = formData.scope?.entityTargeting?.subEntityIds || [];
                      if (!currentIds.includes(selectedOption.value)) {
                        handleSubEntityChange([...selectedSubEntities, selectedOption]);
                      }
                    }
                  }}
                  placeholder={subEntityPlaceholder}
                  isMulti={false}
                  isSearchable
                  menuPortalTarget={document.body}
                  isLoading={locationsLoading}
                  isDisabled={isBookingDomain || !formData.scope?.entityTargeting?.entityIds?.length || locationsLoading}
                />
                {(formData.scope?.entityTargeting?.entityIds?.length || 0) > 0 && subEntityOptions.length > 0 && (
                  <div className="targeting-subentity-help-text mt-1">
                    <small className="text-muted">
                      Showing sub-entities from {selectedEntities.length} selected {selectedEntities.length === 1 ? 'entity' : 'entities'}.
                    </small>
                  </div>
                )}
                {selectedSubEntities.length > 0 && (
                  <div className="targeting-selected-chips mt-2">
                    {selectedSubEntities.map((opt) => (
                      <span key={opt.value} className="targeting-chip">
                        {opt.label}
                        <button
                          type="button"
                          className="targeting-chip-remove"
                          onClick={() => handleRemoveSubEntity(opt.value)}
                          aria-label={`Remove ${opt.label}`}
                        >
                          <i className="ph-x"></i>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>
        )}
      </div>

      {/* Refine what this promo applies to */}
      <div className="targeting-section-card mt-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="targeting-refine-title">
            Refine what this promo applies to
          </div>
          <button
            type="button"
            className="targeting-add-filter-btn"
            onClick={handleAddFilter}
            disabled={!canAddFilter}
          >
            + Add Filter
          </button>
        </div>

        {filters.length === 0 ? (
          <div className="text-center py-4 text-muted">
            <small>No filters added. Click "+ Add Filter" to add a filter.</small>
          </div>
        ) : (
          filters.map((filter, index) => {
            const field = mapApiToField(filter.field);
            const selectedOptions = getSelectedFilterOptions(filter);
            const availableFields = index === 0
              ? fieldOptions
              : fieldOptions.filter(opt => {
                  const firstFilterField = filters[0] ? mapApiToField(filters[0].field) : '';
                  return opt.value !== firstFilterField;
                });

            return (
              <div key={index} className="targeting-filter-row" style={{ marginTop: index > 0 ? '16px' : '0' }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="targeting-filter-label">Filter {index + 1}</div>
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-danger p-0 targeting-filter-remove-btn"
                    onClick={() => handleRemoveFilter(index)}
                    style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', padding: '4px' }}
                  >
                    <img
                      src={getImageUrl('admin/static-assets/icons/general/icon-close.svg')}
                      alt="Remove filter"
                      style={{ width: '16px', height: '16px' }}
                    />
                  </button>
                </div>
                <Row className="align-items-start">
                  <Col lg={4} className="mb-3">
                    <Form.Group className="stepper-form-group">
                      <Form.Label className="stepper-form-label">
                        Field
                      </Form.Label>
                      <Select
                        styles={getSelectStyles()}
                        options={availableFields}
                        value={availableFields.find(opt => opt.value === field) || null}
                        onChange={(selectedOption: any) => handleFilterFieldChange(index, selectedOption)}
                        placeholder="Select Field"
                        isSearchable={false}
                        menuPortalTarget={document.body}
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={2} className="mb-3">
                    <Form.Group className="stepper-form-group">
                      <Form.Label className="stepper-form-label">Operator</Form.Label>
                      <Select
                        styles={getSelectStyles()}
                        options={[
                          { value: '=', label: '=' },
                          { value: 'IN', label: 'IN' },
                          { value: 'NOT_IN', label: 'Not IN' },
                        ]}
                        value={{
                          value: filter.operator || 'IN',
                          label: filter.operator === 'NOT_IN' ? 'Not IN' : filter.operator || 'IN',
                        }}
                        onChange={(selectedOption: any) => {
                          if (selectedOption) {
                            const updatedFilters = [...filters];
                            updatedFilters[index] = {
                              ...filter,
                              operator: selectedOption.value,
                            };
                            onChange({
                              scope: {
                                ...formData.scope,
                                filters: updatedFilters,
                              },
                            });
                          }
                        }}
                        isSearchable={false}
                        menuPortalTarget={document.body}
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={6} className="mb-3">
                    <Form.Group className="stepper-form-group">
                      <Form.Label className="stepper-form-label">Value</Form.Label>
                      <div className="targeting-value-container">
                        <Select
                          styles={getSelectStyles()}
                          isMulti={false}
                          isSearchable
                          menuPortalTarget={document.body}
                          options={
                            field === 'SPORT'
                              ? sportFilterOptions.filter(opt => !selectedOptions.some(sel => sel.value === opt.value))
                              : field === 'CATALOG_CATEGORY'
                              ? categoryFilterOptions.filter(opt => !selectedOptions.some(sel => sel.value === opt.value))
                              : []
                          }
                          value={null}
                          onChange={(selectedOption: any) => {
                            if (selectedOption) {
                              const currentIds = (filter.value as (string | number)[]) || [];
                              if (!currentIds.includes(selectedOption.value)) {
                                handleFilterValueChange(index, [...selectedOptions, selectedOption]);
                              }
                            }
                          }}
                          placeholder={
                            !field
                              ? 'Select a field first'
                              : field === 'SPORT'
                              ? 'Select Sports'
                              : 'Select Categories'
                          }
                          isDisabled={!field}
                        />
                        {selectedOptions.length > 0 && (
                          <div className="targeting-selected-chips">
                            {selectedOptions.map((opt) => (
                              <span key={opt.value} className="targeting-chip">
                                {opt.label}
                                <button
                                  type="button"
                                  className="targeting-chip-remove"
                                  onClick={() => {
                                    const updatedOptions = selectedOptions.filter(o => o.value !== opt.value);
                                    handleFilterValueChange(index, updatedOptions);
                                  }}
                                  aria-label={`Remove ${opt.label}`}
                                >
                                  <i className="ph-x"></i>
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                        {showFilterValidation && field && selectedOptions.length === 0 && (
                          <div className="stepper-invalid-feedback" style={{ marginTop: '4px' }}>
                            Please select at least one value
                          </div>
                        )}
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            );
          })
        )}
      </div>

      <div className="d-flex justify-content-between mt-4">
        <button className="stepper-back-button" onClick={onPrevious}>
          <i className="ph-arrow-left"></i>
          Back
        </button>
        <button
          className="stepper-next-button"
          onClick={() => {
            if (!isFiltersValid) {
              setShowFilterValidation(true);
            } else {
              onNext();
            }
          }}
          disabled={!isFiltersValid}
        >
          Next
          <i className="ph-arrow-right"></i>
        </button>
      </div>
    </div>
  );
};

export default StepTargeting;
